import type { Capitolo } from '../tipi.ts'

const capitolo: Capitolo = {
  id: 'mediazioni',
  titolo: 'Mediazioni e «Le mie mediazioni»',
  gruppo: 'Mediazioni',
  permesso: ['mediazione.assegna', 'mediazione.propria'],
  aCheServe: 'Le mediazioni linguistico-culturali si programmano dalla scheda della persona o dal «Calendario delle '
    + 'mediazioni». La mediatrice o il mediatore le trova in «Le mie mediazioni» e in «Il mio calendario», completa '
    + 'i dati che mancano e segna com’è andata.',
  parti: [
    {
      titolo: 'Programmare una mediazione',
      permesso: 'mediazione.assegna',
      passi: [
        'Nella scheda apri la linguetta «Mediazioni» e premi «Programma una mediazione».',
        'In «Chi fa la mediazione» scegli la «Lingua» e il «Mediatore»: accanto al nome c’è il comune in cui lavora. '
          + 'In cima ci sono quelli che «Lavorano in questa sede». Sotto il nome leggi se usa il gestionale.',
        'In «Quando» scegli giorno, ora d’inizio e durata prevista.',
        'In «Dove» scegli la sede e, se serve, il «Luogo preciso» (per esempio «Questura, ufficio immigrazione»). Se '
          + 'la sede ha stanze, scegli la «Stanza»: una stanza occupata blocca.',
        'Se la mediazione si fa per un ente, sceglilo in «Ente»: lo vede solo chi segue la scheda.',
        'Per un incontro fisso, in «Si ripete» scegli «Ogni settimana», «Ogni due settimane» o «Ogni mese, nello '
          + 'stesso giorno» e quante volte: al più 26.',
        'Se vuoi, scrivi una «Nota per il mediatore».',
        'In «Dove finiscono le ore» scegli il percorso di mediazione della persona. Premi «Programma».',
      ],
      immagine: {
        file: 'mediazioni-programma.webp', larghezza: 1200, altezza: 800,
        descrizione: 'La finestra Programma una mediazione con lingua, mediatore, giorno, ora e sede.',
      },
      nota: 'Il mediatore riceve un avviso e un’email con giorno, ora, lingua e sede, mai il nome della persona; '
        + 'per una serie, un avviso e un’email soli. L’email porta il file .ics da aggiungere al calendario. '
        + 'Se ha un recapito nell’albo, il messaggio dopo «Programma» e la riga della mediazione mostrano «Recapito del '
        + 'mediatore»: dove riceve, da dire alla persona. Non è il luogo della mediazione.',
    },
    {
      titolo: 'Spostare o annullare',
      permesso: 'mediazione.assegna',
      passi: [
        '«Sposta» cambia giorno, ora, sede o mediatore: chi è coinvolto riceve l’avviso.',
        '«Annulla» chiede il motivo, che resta nella scheda. In una serie, «Annulla anche le successive» annulla anche '
          + 'le mediazioni dopo quella scelta, con un avviso solo al mediatore.',
        'Dal giorno della mediazione, «Non si è presentata» la chiude senza ore. Non si torna indietro.',
        'Per i mediatori che non usano il gestionale, l’esito lo segni tu con «Segna come svolta».',
      ],
    },
    {
      titolo: 'Dopo la mediazione: l’intervento in bozza',
      permesso: 'mediazione.assegna',
      testo: [
        'Quando la mediazione è segnata come svolta, sul percorso scelto nasce da solo un intervento «Mediazione '
          + 'linguistico-culturale» in bozza, a nome di chi l’ha programmata, che riceve l’avviso.',
        'Lo completi dalla pagina «Interventi» (stato «Da completare», «Solo i miei»): aggiungi comune, '
          + 'categoria, ente coinvolto e finanziamento, poi «Salva».',
      ],
    },
    {
      titolo: 'Il «Calendario delle mediazioni»',
      permesso: 'agenda.leggi',
      testo: [
        'Nel menu, «Calendario delle mediazioni»: la settimana con una riga per mediatore. In «Una riga per» puoi '
          + 'raggruppare per lingua; i filtri sono «Mediatore», «Lingua», «Sede» ed «Ente». Con una sede scelta e più '
          + 'di 15 mediatori, in riga ci sono quelli che hanno lavorato nella sede di recente: «Mostra tutti» mostra '
          + 'gli altri.',
        'Il nome della persona compare solo a chi segue la scheda; per gli altri la mediazione è «Mediazione», con '
          + 'mediatore e lingua. Le mediazioni dello stesso mediatore in sedi che non vedi compaiono come «Occupato in '
          + 'un’altra sede».',
        'Rosso: lo stesso mediatore ha un’altra mediazione nello stesso orario. Arancione: sede chiusa, fuori orario '
          + 'o mediatore che si è segnato non disponibile.',
      ],
      passi: [
        'Fai clic su uno spazio vuoto, oppure premi «Programma una mediazione»: mediatore, giorno e ora sono già '
          + 'compilati.',
        'In «Cerca la scheda» scrivi nome, cognome o numero della scheda e sceglila; poi si apre la finestra di sempre.',
        'Una mediazione si apre con un clic: per spostarla o annullarla apri la scheda, linguetta «Mediazioni».',
        'Per un mediatore che non usa il gestionale scegli il «Mediatore» e premi «Scarica il file .ics»: è la sua '
          + 'settimana, da mandargli.',
      ],
    },
    {
      titolo: '«Il mio calendario»',
      permesso: 'mediazione.propria',
      passi: [
        'Nel menu, «Il mio calendario»: le tue mediazioni in settimana, giorno, mese o elenco, senza i nomi. Un clic '
          + 'apre la mediazione.',
        '«Scarica il file per il calendario (.ics)» porta le tue mediazioni, da 30 giorni fa a 180 avanti, nel '
          + 'calendario del telefono o di Outlook. Nella pagina della mediazione, «Aggiungi al calendario (.ics)» porta '
          + 'quella sola.',
        'Se CIDIS ha acceso il calendario in abbonamento, in «Il mio profilo», «Il calendario in Outlook e sul '
          + 'telefono», crei un collegamento a «Le mie mediazioni» che il telefono rilegge da solo.',
        'Se un giorno non ci sei, premi «Segnati non disponibile»: chi programma le mediazioni lo vede, senza motivo. '
          + 'Sotto il calendario, «Quando non sei disponibile» elenca i giorni segnati; «Togli» li cancella.',
      ],
    },
    {
      titolo: '«Le mie mediazioni»',
      permesso: 'mediazione.propria',
      passi: [
        'Nel Cruscotto trovi «Le tue prossime mediazioni». La voce di menu «Le mie mediazioni» mostra quelle in '
          + 'programma e quelle concluse nell’ultimo mese.',
        'Premi una mediazione: si apre «Mediazione con …» con giorno, ora, durata, lingua, sede, luogo e la nota '
          + 'di chi l’ha programmata.',
        'Il riquadro «I dati di …» dice quali dati della persona mancano. Non mostra i dati già inseriti.',
      ],
      immagine: {
        file: 'le-mie-mediazioni.webp', larghezza: 1200, altezza: 800,
        descrizione: 'La pagina Le mie mediazioni con le mediazioni in programma, giorno e ora in evidenza.',
      },
    },
    {
      titolo: 'Completare i dati durante l’incontro',
      permesso: 'mediazione.propria',
      passi: [
        'Nella pagina della mediazione premi «Completa i dati di …».',
        'Compare una sezione alla volta, solo dove mancano dati: chi è, codice fiscale, dove abita e come si '
          + 'contatta, lingue, come ha conosciuto il servizio.',
        'Compila quello che la persona sa e premi «Salva e vai avanti». Se non lo sa, «Salta per ora».',
      ],
      nota: 'Si può fare finché la mediazione è in programma e fino a sette giorni dopo.',
    },
    {
      titolo: 'Segnare com’è andata',
      permesso: 'mediazione.propria',
      passi: [
        'Dal giorno dell’incontro, nel riquadro «L’incontro» premi «Segna come svolta».',
        'Scegli «Quanto è durata», a mezz’ore, e conferma.',
        'Se la persona non è venuta, premi invece «La persona non si è presentata».',
      ],
    },
  ],
  avvertenze: [
    {
      quando: 'La finestra dice che la persona non ha un percorso di mediazione.',
      cosaFare: 'Premi «Apri il percorso di mediazione», se compare. Altrimenti chiedi a chi segue la persona.',
    },
    {
      quando: 'Il mediatore è già impegnato in quell’orario.',
      cosaFare: 'Scegli «Programma lo stesso» oppure «Scelgo un altro orario».',
    },
    {
      quando: '«Il mediatore si è segnato non disponibile …»',
      cosaFare: 'Scegli un altro mediatore o un altro giorno, oppure «Programma lo stesso».',
    },
    {
      quando: 'La sede non è aperta in quell’orario.',
      cosaFare: 'Cade in una festività, in una chiusura o fuori orario: scegli un altro orario oppure «Programma lo '
        + 'stesso».',
    },
    {
      quando: 'La stanza è occupata.',
      cosaFare: 'La stanza occupata blocca: scegli un’altra stanza, nessuna stanza o un altro orario.',
    },
    {
      quando: '«Non usa il gestionale: va avvisato a voce»',
      cosaFare: 'Il mediatore non riceve avvisi né email: chiamalo tu.',
    },
    {
      quando: 'La mediatrice o il mediatore non vede le sue mediazioni.',
      cosaFare: 'Servono l’incarico di Mediatore e il collegamento fra la sua voce dell’albo e la sua utenza: '
        + 'chiedi a chi gestisce l’albo di usare «Dai l’accesso».',
    },
    {
      quando: 'Il mediatore vuole annullare.',
      cosaFare: 'Il mediatore non annulla: lo fa chi ha programmato la mediazione.',
    },
  ],
  vediAnche: ['albo-mediatori', 'percorsi-interventi', 'agenda', 'telefono'],
  parole: ['mediatore', 'mediatrice', 'interprete', 'lingua', 'ore di mediazione', 'svolta', 'non presentata',
    'recapito', 'calendario', 'ics', 'outlook', 'serie', 'ripetizione', 'stanza', 'ente'],
}

export default capitolo
